import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '@auth0/auth0-angular';
import {catchError, finalize, from, map, Observable, of, switchMap, take, tap} from 'rxjs';
import { AuthorizationService } from '../services/authorization.service';
import * as JwtDecode from 'jwt-decode';
import { capitalizeAllFirstLetters } from '../utils/strings';
import { StateService } from '../services/state.service';
import {StorageService} from '../services/storage.service';
import {LocalStorageKeys} from '../shared/types/storage';
import {ManagementService} from '../components/management-screen/management.service';
import {Permissions} from '../shared/permissions';

function initializeUserProfile( token: string, authorization: AuthorizationService, state: StateService, storage : StorageService, managementService: ManagementService): Observable<void> {
  if (authorization.profileInitiated) {
    return of(void 0);
  }
  state.loading = true;
  const decodedToken = JwtDecode.jwtDecode(token) as Record<string, any>;
  authorization.permissions = decodedToken['permissions'];
  return state.getMyUser().pipe(
    switchMap(user => {
      const { id, email, firstName, lastName, account, role, trialDaysLeft } = user;
      authorization.userProfile = {
        id: id as string,
        email: email as string,
        firstName: capitalizeAllFirstLetters(firstName),
        lastName: capitalizeAllFirstLetters(lastName),
        role: role,
        account: account.key,
        trialDaysLeft: trialDaysLeft ?? null
      };

      if (authorization.hasPermissions([Permissions.READ_ACCOUNT])) {
        return managementService.getAccounts({ elaborate: false }).pipe(
          map(accounts => ({
            user,
            accountKeys: accounts.accounts.map(acc => acc.accountKey),
          }))
        );
      } else {
        return of({
          user,
          accountKeys: [account?.key],
        });
      }
    }),
    tap(({ accountKeys }) => {
      const sessionAccount = storage.local.getItem(LocalStorageKeys.ACCOUNT);
      state.accounts = accountKeys;

      const activeOrg =
        sessionAccount && accountKeys.includes(sessionAccount)
          ? sessionAccount
          : (accountKeys ? accountKeys[0]: undefined);
      state.setActiveAccount(activeOrg);
    }),
    finalize(() => {
      state.loading = false;
    }),
    map(() => void 0)
  );
}

export const authGuard: CanActivateFn = (route, state) => {
  const auth = inject(AuthService);
  const authorization = inject(AuthorizationService);
  const managementService = inject(ManagementService);
  const stateService = inject(StateService);
  const router = inject(Router);
  const storageService = inject(StorageService);
  const requiredPermissions = route.data?.['permissions'] as string[] || [];
  const redirectTo = route.data?.['redirectTo'];
  const forceLogout = !redirectTo;

  return auth.isAuthenticated$.pipe(
    take(1),
    switchMap(isAuthenticated => {
      if (!isAuthenticated) {
        auth.loginWithRedirect({
          appState: { target: state.url },
          authorizationParams: { scope: 'openid profile' }
        });
        return of(false);
      }
      return from(auth.getAccessTokenSilently()).pipe(
        switchMap(token =>
          initializeUserProfile(token, authorization, stateService, storageService, managementService).pipe(
            map(() => {
              const hasAccess = authorization.hasPermissions(requiredPermissions);
              if (!hasAccess) {
                if (forceLogout) {
                  auth.logout({ logoutParams: { returnTo: window.location.origin } });
                } else {
                  router.navigateByUrl(redirectTo!);
                }
                return false;
              }
              return true;
            })
          )
        ),
        catchError(() => {
          auth.logout({ logoutParams: { returnTo: window.location.origin } });
          return of(false);
        })
      );
    })
  );
}
